// @ts-ignore - Dynamic import for Vue component

import {
  createSpeakerRequest,
  offStageSpeakerRequest,
  muteSpeakerAudioSpeakerRequest,
  partialUpdateSpeakerRequest,
  askToUnmuteSpeakerRequest,
  unmuteSpeakerAudioSpeakerRequest,
  reorderBroadcastUsers
} from '@goldcast/api/backend';
import { GenericMessageDialog } from 'uicomponents';

import DialogService from './dialogs/DialogService';
import logger from './logger.service';

import analytics from '@/analytics';
import ConfirmationPopup from '@/components/core/ConfirmationPopup.vue';
import { AGORA, getStaticAssetsUrl } from '@/constants';
import store from '@/store';
import { Broadcast, SpeakerStageModeEnum } from '@/store/modules/event/types';
import { pushRoute } from '@/utils/routerAccess';

export const DELAY_BEFORE_AUTO_CANCEL = 5000;

export const SoundType = {
  PUSH_TO_STAGE: 'push-to-stage.mp3',
  ASK_TO_UNMUTE: 'notification.mp3',
  MUTE: 'mute.mp3'
} as const;

export function playSound(soundUrlOrType: string): void {
  try {
    const soundUrl = soundUrlOrType.startsWith('http') ? soundUrlOrType : getStaticAssetsUrl(soundUrlOrType);
    const audio = new Audio(soundUrl);
    audio.play().catch(error => {
      logger.warn(`Failed to play sound: ${soundUrlOrType}`, error);
    });
  } catch (error) {
    logger.warn(`Failed to create audio for sound: ${soundUrlOrType}`, error);
  }
}

export type SpeakerStageRequest = {
  broadcast: string;
  created_at: string;
  id: string;
  speaker: string;
  status: 'ACTIVE' | 'COMPLETED' | 'ORG_CANCELLED' | 'EXPIRED' | 'SPEAKER_CANCELLED';
  updated_at: string;
};

export function moveSpeakerOnStage(broadcastId: string, speakerId: string) {
  return createSpeakerRequest({
    body: {
      broadcast: broadcastId,
      speaker: speakerId
    }
  });
}

export async function pushSpeakerToStage(broadcastId: string, speakerId: string) {
  try {
    return await createSpeakerRequest({
      body: {
        broadcast: broadcastId,
        speaker: speakerId
      }
    });
  } catch (error) {
    logger.error('Failed to push speaker to stage', { broadcastId, speakerId, error });
    throw error;
  }
}

export function moveSpeakerOffStage(broadcastId: string, speakerId: string) {
  return offStageSpeakerRequest({
    body: {
      broadcast: broadcastId,
      speaker: speakerId
    }
  });
}

export function muteSpeakerAudio(broadcastId: string, speakerId: string) {
  return muteSpeakerAudioSpeakerRequest({
    body: {
      broadcast: broadcastId,
      speaker: speakerId
    }
  });
}

export function askToUnmuteSpeaker(broadcastId: string, speakerId: string) {
  return askToUnmuteSpeakerRequest({
    body: {
      broadcast: broadcastId,
      speaker: speakerId
    }
  });
}

export function unmuteSpeakerAudio(broadcastId: string, speakerId: string) {
  return unmuteSpeakerAudioSpeakerRequest({
    body: {
      broadcast: broadcastId,
      speaker: speakerId
    }
  });
}

export function updateOnStageRequest(speakerId: string, status: 'COMPLETED' | 'ORG_CANCELLED' | 'SPEAKER_CANCELLED') {
  analytics.track('on_stage_request_update', { status, speakerId });

  const requestId = store.state.event.stageControl.speakerStageRequests.find(
    request => request.speaker === speakerId
  )?.id;

  if (!requestId) {
    throw new Error('No speaker stage request found');
  }

  store.commit('event/setShouldForceMoveToStage', false);

  return partialUpdateSpeakerRequest({
    id: requestId,
    body: {
      status
    }
  });
}

export function currentUserHasStageRequest(broadcastId: string) {
  const currentUserId = store.state.user.currentUser?.id;
  return store.state.event.stageControl.speakerStageRequests.some(
    request => request.speaker === currentUserId && request.broadcast === broadcastId
  );
}

export function showStageInvitationDialog(speakerStageRequest: SpeakerStageRequest) {
  const i18n = store.state.event.translator?.i18n ?? { global: { t: (k: string) => k } };
  const broadcastTitle = store.state.event.event.agenda.broadcasts.find(
    (broadcast: Broadcast) => broadcast.id === speakerStageRequest.broadcast
  )?.title;
  const isRecording = store.state.event.event.event_type === 'Recording';
  const moveToStageMessage = isRecording
    ? 'If you click to accept the request, you will be on stage and captured in the recording'
    : 'If you click to accept the request, you will be live and visible to event attendees.';

  return DialogService.show(
    GenericMessageDialog,
    {
      title: i18n.global.t('@@translate@@ Move to stage'),
      message: i18n.global.t(
        '@@translate@@ The event organizer has requested to move you to the stage for <strong>{broadcastTitle}</strong>.<br /><br />{moveToStageMessage}',
        { broadcastTitle, moveToStageMessage }
      ),
      resolveLabel: i18n.global.t('@@translate@@ Go On Stage')
    },
    {
      cancelOnClickOutside: false,
      on: {
        onHide: () => {
          return new Promise(() => {
            let promise: Promise<any> = Promise.resolve();

            if (store.state.route.params.id !== speakerStageRequest.broadcast) {
              promise = pushRoute({
                name: 'Broadcast',
                params: {
                  id: speakerStageRequest.broadcast
                }
              });
            }

            promise.then(() => {
              store.commit('event/setShouldForceMoveToStage', true);
            });
          });
        },
        cancel: () => {
          return new Promise(() => {
            updateOnStageRequest(speakerStageRequest.speaker, 'SPEAKER_CANCELLED');
          });
        }
      }
    }
  );
}

export function showKickedFromStageDialog() {
  const speakerStageMode = store.state.event.event?.speaker_stage_mode;
  if (speakerStageMode !== SpeakerStageModeEnum.INVITE_SPEAKER) {
    return;
  }

  const i18n = store.state.event.translator?.i18n ?? { global: { t: (k: string) => k } };
  DialogService.show(
    GenericMessageDialog,
    {
      title: i18n.global.t('@@translate@@ Moved to backstage'),
      message: i18n.global.t(
        '@@translate@@ The organizer has removed you from the stage. Please do not try to rejoin without contacting the organizer.'
      ),
      resolveLabel: i18n.global.t('@@translate@@ Okay')
    },
    { cancelOnClickOutside: true, on: {} }
  );
}

export function showMutedByOrganizerDialog() {
  const i18n = store.state.event.translator?.i18n ?? { global: { t: (k: string) => k } };
  playSound(SoundType.MUTE);
  DialogService.show(
    ConfirmationPopup,
    {
      title: i18n.global.t('@@translate@@ You have been muted'),
      type: 'warning'
    },
    {
      cancelOnClickOutside: true,
      autoCancelTimeout: DELAY_BEFORE_AUTO_CANCEL,
      on: {}
    }
  );
}

export function showUnmutedByOrganizerDialog() {
  const i18n = store.state.event.translator?.i18n ?? { global: { t: (k: string) => k } };
  playSound(SoundType.MUTE);
  DialogService.show(
    ConfirmationPopup,
    {
      title: i18n.global.t('@@translate@@ You have been unmuted'),
      type: 'warning'
    },
    {
      cancelOnClickOutside: true,
      autoCancelTimeout: DELAY_BEFORE_AUTO_CANCEL,
      on: {}
    }
  );
}

export async function showAskToUnmuteDialog() {
  const i18n = store.state.event.translator?.i18n ?? { global: { t: (k: string) => k } };

  playSound(SoundType.ASK_TO_UNMUTE);

  const dialogRef = DialogService.show(
    ConfirmationPopup,
    {
      title: i18n.global.t('@@translate@@ The host would like you to unmute'),
      cancelText: i18n.global.t('@@translate@@ Stay muted'),
      confirmText: i18n.global.t('@@translate@@ Unmute'),
      type: 'warning'
    },
    {
      cancelOnClickOutside: false,
      blockBackgroundActions: false,
      closeOnCTAClick: false,
      on: {
        onConfirm: async () => {
          const currentBroadcastId = store.state.route.params.id as string;
          if (currentBroadcastId) {
            const channelName =
              store.state.event.useNewMuteUnmuteCapabilities && !store.getters['mediaroom/isCurrentUserOnStage']
                ? currentBroadcastId + AGORA.UID_SUFFIX.BACKSTAGE
                : currentBroadcastId;
            store.commit('mediaroom/setStageControl', { audio: true }, { root: true });
            await store.dispatch(
              'mediaroom/createLocalTrack',
              {
                channelName,
                mediaTrackType: 'audio',
                shouldMute: false
              },
              { root: true }
            );
          }
          DialogService.close(dialogRef.index);
        },
        onCancel: () => {
          DialogService.close(dialogRef.index);
        }
      }
    }
  );

  return dialogRef;
}

export function updateSpeakerRanks(broadcastId: string, userRanks: Array<{ user_id: string; stage_rank: number }>) {
  return reorderBroadcastUsers({
    id: broadcastId,
    body: {
      user_ranks: userRanks
    }
  });
}
